import type { KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@repo/tokens";
import { useIsMobile, useIsTablet } from "@repo/utils";
import { Box } from "../../primitives/box";
import { Text } from "../../primitives/text";
import { Icon } from "../icon";
import { Modal } from "../modal";
import { useLogoutConfirmModalStyles } from "./styles";

export type LogoutConfirmModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

export const LogoutConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
}: LogoutConfirmModalProps) => {
  const { theme } = useTheme();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isNarrowLayout = isMobile || isTablet;
  const styles = useLogoutConfirmModalStyles(isNarrowLayout);
  const { t } = useTranslation("common");

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  const handleKeyActivate = (event: KeyboardEvent, action: () => void) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      action();
    }
  };

  const body = (
    <Box style={styles.logoutModalBody}>
      <Box
        role="button"
        tabIndex={0}
        aria-label={t("menuDrawer.close")}
        onClick={onClose}
        onKeyDown={(event) => handleKeyActivate(event, onClose)}
        style={styles.logoutModalClose}
      >
        <Icon name="close" size={22} color={theme.action.primary.default} />
      </Box>

      <Box style={styles.logoutIconSection}>
        <Box mb={20}>
          <Icon
            name="log_out"
            width={56}
            height={56}
            color={theme.feedback.error}
          />
        </Box>

        <Text style={styles.logoutText}>
          {t("menuDrawer.sure_want_logout")}
        </Text>

        <Box
          role="button"
          tabIndex={0}
          onClick={handleConfirm}
          onKeyDown={(event) => handleKeyActivate(event, handleConfirm)}
          style={styles.logoutOutlineButton}
        >
          <Text style={styles.logoutOutlineButtonText}>
            {t("menuDrawer.logout")}
          </Text>
        </Box>

        <Box
          role="button"
          tabIndex={0}
          onClick={onClose}
          onKeyDown={(event) => handleKeyActivate(event, onClose)}
        >
          <Text style={styles.logoutCloseLink}>{t("menuDrawer.close")}</Text>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Modal
      isModalOpen={isOpen}
      onClose={onClose}
      showHeader={false}
      showFooter={false}
      body={body}
      height="auto"
      width={isNarrowLayout ? "100vw" : 420}
      overLayColor={theme.surface.overlay}
      justifyContent={isNarrowLayout ? "flex-end" : "center"}
      alignItems="center"
      styles={{
        overlay: isNarrowLayout ? styles.overlayLogout : undefined,
        modal: {
          borderTopLeftRadius: isNarrowLayout ? 40 : "1.5rem",
          borderTopRightRadius: isNarrowLayout ? 40 : "1.5rem",
          borderBottomLeftRadius: isNarrowLayout ? undefined : "1.5rem",
          borderBottomRightRadius: isNarrowLayout ? undefined : "1.5rem",
          ...(isNarrowLayout
            ? { backgroundColor: theme.colors.background[50] }
            : {}),
        },
      }}
    />
  );
};

LogoutConfirmModal.displayName = "LogoutConfirmModal";
